// =============================================================================
// iTravel 2.0 — AuthGuard
// =============================================================================
// Protects routes that require authentication. If the user is not logged in,
// they are redirected to the login page with a `returnTo` query parameter so
// they can be sent back after successful authentication.
// =============================================================================

import { type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, useLocation } from 'react-router-dom';

import { FullScreenLoader } from '@/components/feedback/FullScreenLoader';
import {
  selectAuthStatus,
  selectIsAuthenticated,
  selectIsHydrated,
  useAuthStore,
} from '@/store/auth.store';

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface AuthGuardProps {
  /** The protected content to render when the user is authenticated */
  children: ReactNode;
  /** Optional override for the redirect path (defaults to /login) */
  redirectTo?: string;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/**
 * AuthGuard wraps protected routes and ensures the user is authenticated
 * before rendering the children. It handles three states:
 *
 * 1. **Hydrating / Loading** — The auth store is still restoring persisted
 *    state or validating the token. A full-screen loader is shown.
 *
 * 2. **Authenticated** — The user is logged in. Children are rendered.
 *
 * 3. **Unauthenticated** — The user is not logged in. They are redirected
 *    to the login page with a `returnTo` search param encoding the current
 *    location so they can be navigated back after login.
 */
export function AuthGuard({ children, redirectTo = '/login' }: AuthGuardProps) {
  const isAuthenticated = useAuthStore(selectIsAuthenticated);
  const isHydrated = useAuthStore(selectIsHydrated);
  const status = useAuthStore(selectAuthStatus);
  const location = useLocation();
  const { t } = useTranslation();

  // ---- State 1: Still loading / hydrating ----
  // The auth store persists the refresh token and basic user info to
  // localStorage. On app start we need to wait for that data to be restored
  // and for the initial token validation to complete before making a
  // routing decision.
  if (!isHydrated || status === 'idle' || status === 'loading') {
    return <FullScreenLoader message={t('auth.guard.checkingAuth')} />;
  }

  // ---- State 2: Authenticated ----
  if (isAuthenticated) {
    return <>{children}</>;
  }

  // ---- State 3: Unauthenticated — redirect to login ----
  // Encode the current location (pathname + search + hash) so the login
  // page can redirect back here after a successful authentication.
  const returnTo =
    location.pathname === '/'
      ? undefined
      : `${location.pathname}${location.search}${location.hash}`;

  const searchParams = new URLSearchParams();
  if (returnTo) {
    searchParams.set('returnTo', returnTo);
  }

  const loginPath = returnTo ? `${redirectTo}?${searchParams.toString()}` : redirectTo;

  return <Navigate to={loginPath} replace />;
}

export default AuthGuard;
